import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ExpenseEditPage.css';
import { getExpenseTypes, updateExpenseStatus, createExpense } from '../../api/expenses';


// Mock data for demonstration (fallback only)
const mockExpenses = [
  {
    id: 'EXP001',
    project: 'Project Alpha',
    expenseType: 'Travel',
    amount: 250.0,
    date: '2024-03-15',
    status: 'approved',
    description: 'Flight ticket to client meeting',
    receipts: ['receipt1.pdf']
  },
  {
    id: 'EXP002',
    project: 'Project Beta',
    expenseType: 'Meals',
    amount: 75.5,
    date: '2024-03-14',
    status: 'pending',
    description: 'Business lunch with client',
    receipts: []
  },
  {
    id: 'EXP003',
    project: 'Project Gamma',
    expenseType: 'Lodging',
    amount: 350.0,
    date: '2024-03-13',
    status: 'rejected',
    description: 'Hotel accommodation',
    receipts: ['receipt2.pdf']
  }
];

const statusColors = {
  draft: '#bdbdbd',
  pending: '#1976d2',
  approved: '#388e3c',
  rejected: '#d32f2f',
};

const expenseTypes = [
  'Travel',
  'Meals',
  'Lodging',
  'Office Supplies',
  'Software',
  'Equipment',
  'Other'
];

const ExpenseEditPage = ({ mode }) => {
  const location = useLocation();
  const { id: paramId } = useParams();
  const navigate = useNavigate();

  // ✅ row data + filter state passed from ExpenseSheets
  const passedExpense = location.state?.expense || null;
  const from = location.state?.from || null;
  const to = location.state?.to || null;

 const [form, setForm] = useState({
  project: '',
  expenseType: '',
  amount: '',
  date: null,
  description: '',
  status: 'draft',
});

const [isEditingDisabled, setIsEditingDisabled] = useState(false);

// Step 3B additions
const [header, setHeader] = useState({
  orgName: '',
  costCenter: '',
  deptName: '',
  userName: '',
  approverName: '',
});

const [expenseTypeOptions, setExpenseTypeOptions] = useState([]);
console.log("EXPENSE TYPE OPTIONS:", expenseTypeOptions);



// Validation errors
const [errors, setErrors] = useState({});


  // ✅ Task #6: Project dropdown from Redux user data
  const reduxProjects = useSelector((state) => {
  const p1 = state?.user?.projects;
  if (Array.isArray(p1)) return p1;

  const p2 = state?.auth?.user?.projects;
  if (Array.isArray(p2)) return p2;

  return [];
});


  // fallback list if redux doesn't have projects
  const projectOptions = [
    'Project Alpha',
    'Project Beta',
    'Project Gamma',
    'Project Delta',
  ];

// Take project from the expense itself
const rawProject = passedExpense?._raw?.project ? [passedExpense._raw.project] : [];

// Merge redux projects + expense project
const projectsToUse =
  (reduxProjects && reduxProjects.length > 0)
    ? [...reduxProjects, ...rawProject]
    : rawProject;


  // Existing ID logic (used for new expense mode)
  const existingIds = mockExpenses.map(e => e.id);
  function generateNewId() {
    let num = 1;
    let newId = '';
    const idSet = new Set(existingIds);
    while (true) {
      newId = `EXP${num.toString().padStart(3, '0')}`;
      if (!idSet.has(newId)) break;
      num++;
    }
    return newId;
  }

  const id = paramId || generateNewId();
// ✅ Load expense types for Add page too
useEffect(() => {
  // If already loaded (edit page loads them), skip
  if (expenseTypeOptions.length > 0) return;

  (async () => {
    try {
      const types = await getExpenseTypes({ orgId: 1 });
      setExpenseTypeOptions(types || []);
    } catch (e) {
      console.error("Failed to load expense types", e);
    }
  })();
}, []); 

 useEffect(() => {
  const params = new URLSearchParams(location.search);
  const viewOnly = params.get('viewOnly') === 'true';

  // ✅ When coming from list page
  if ((mode === 'edit' || mode === 'view') && passedExpense) {
    const raw = passedExpense._raw || {};
  console.log("COST CENTER OBJ:", raw?.department?.costCenter);
console.log("COST CENTER KEYS:", Object.keys(raw?.department?.costCenter || {}));
    console.log("RAW:", raw);


    // --- Header fields ---
    const userFirst = raw?.user?.firstName ?? raw?.firstName ?? "";
    const userLast = raw?.user?.lastName ?? raw?.lastName ?? "";
    const approverFirst = raw?.approver?.firstName ?? "";
    const approverLast = raw?.approver?.lastName ?? "";

   // Cost center object from backend
const costCenterObj = raw?.department?.costCenter;

// Pick a readable cost center label
const costCenterText =
  costCenterObj?.ccName ??
  costCenterObj?.costCenterName ??
  costCenterObj?.costCenterCode ??
  costCenterObj?.costCenterNum ??
  costCenterObj?.name ??
  costCenterObj?.code ??
  "";

// Set header using actual backend structure
setHeader({
  orgName: raw?.organization?.orgName ?? "",
  costCenter: costCenterText,
  deptName: raw?.department?.deptName ?? "",
  userName: `${userFirst} ${userLast}`.trim(),
  approverName: `${approverFirst} ${approverLast}`.trim(),
});


    // --- Form fields ---
    setForm({
  project: raw?.project?.projectCode ?? "",
  expenseType:
  raw?.expenseType?.expenseTypeName ??
  raw?.expenseType?.expTypeName ??
  raw?.expenseType?.typeName ??
  raw?.expenseType?.name ??
  passedExpense.expenseType ??
  "",

  amount: passedExpense.amount ?? "",
  date: raw?.expenseDate
    ? new Date(raw.expenseDate)
    : (passedExpense.date ? new Date(passedExpense.date) : null),
  description: raw?.description ?? passedExpense.description ?? "",
  status: passedExpense.status ?? "draft",
});

  // --- Load expense type dropdown options ---
(async () => {
  try {
    const types = await getExpenseTypes({ orgId: 1 });

    // ✅ also include the expense's current type so preselect always works
    const currentType = raw?.expenseType ? [raw.expenseType] : [];

    const merged = [...(types || []), ...currentType];
const seen = new Set();

setExpenseTypeOptions(
  merged.filter((t) => {
    const name =
      t?.expenseTypeName ??
      t?.expTypeName ??
      t?.typeName ??
      t?.name ??
      "";
    if (!name) return false;
    if (seen.has(name)) return false;
    seen.add(name);
    return true;
  })
);

  } catch (e) {
    console.error("Failed to load expense types", e);
  }
})();

    // --- Disable edit logic ---
    if (viewOnly) setIsEditingDisabled(true);
    else {
      const status = (passedExpense.status || "draft").toLowerCase();
      setIsEditingDisabled(!(status === "draft" || status === "rejected"));
    }

    return;
  }

  // --- Fallback mock data (keep this) ---
  if (mode === 'edit' && id) {
    const found = mockExpenses.find((e) => String(e.id) === String(id));
    if (found) {
      setForm({
        project: found.project,
        expenseType: found.expenseType,
        amount: found.amount,
        date: new Date(found.date),
        description: found.description,
        status: found.status,
      });

      if (viewOnly) setIsEditingDisabled(true);
      else if (found.status === 'draft' || found.status === 'rejected') setIsEditingDisabled(false);
      else setIsEditingDisabled(true);
    }
  }
}, [mode, id, location.search, passedExpense]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'amount') {
      const formatted = value.replace(
        /^(\d*)(\.(\d{0,2})?)?.*$/,
        (m, int, dot, dec) => int + (dot ? dot : '')
      );
      setForm((prev) => ({ ...prev, amount: formatted }));
    } else if (name === 'description') {
      setForm((prev) => ({ ...prev, description: value.slice(0, 512) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDateChange = (date) => setForm((prev) => ({ ...prev, date }));

function validateRequired(f) {
  const e = {};
  if (!String(f.project || "").trim()) e.project = "Project is required";
  if (!String(f.expenseType || "").trim()) e.expenseType = "Expense type is required";
  if (!String(f.amount || "").trim()) e.amount = "Amount is required";
  if (!f.date) e.date = "Date is required";
  return e;
}

// ===== Auth helpers =====
function readAuth() {
  try {
    const raw = localStorage.getItem("ezts_auth");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getAuthUser() {
  const obj = readAuth();
  return obj?.user ?? null;
}

// ===== Build POST payload for create expense =====
function buildCreatePayload() {
  const authUser = getAuthUser();

  const userId = authUser?.userId ?? null;
  const approverId = authUser?.approver?.userId ?? null;
  const deptId = authUser?.department?.deptId ?? null;
  const orgId = authUser?.department?.costCenter?.organization?.orgId ?? null;

  // selected project from dropdown (PRJ001 etc.)
  const selectedProject = projectsToUse.find((p) => p.projectCode === form.project);
  const projectId = selectedProject?.projectId ?? null;

  // selected expense type from dropdown
  const selectedType = expenseTypeOptions.find((t) => t.expenseTypeName === form.expenseType);
  const expenseTypeCode = selectedType?.expenseTypeCode ?? null;

  return {
    // required fields
    expenseAmount: Number(form.amount),
    expenseDate: form.date.toISOString().slice(0, 10),
    description: form.description,
    status: 1, // draft on Save (Submit will change to 2 later)

    // nested objects (minimum ids)
    project: { projectId },
    expenseType: { expenseTypeCode },
    approver: { userId: approverId },
    department: { deptId },
    organization: { orgId },

    // audit
    createUid: userId,
    updateUid: userId,
  };
}


const handleSave = async () => {
  const e = validateRequired(form);
  setErrors(e);
  if (Object.keys(e).length > 0) return;

  // ✅ NEW EXPENSE (Add page)
  if (!passedExpense) {
    try {
      const payload = buildCreatePayload();
      await createExpense(payload);
      navigate('/expenses');
    } catch (err) {
      console.error(err);
      setErrors({ api: "Save failed. Please try again." });
    }
    return;
  }

  // ✅ EXISTING EXPENSE (Edit page)
  try {
    await updateExpenseStatus({
      expenseId: passedExpense._raw.expenseId,
      status: 1, // draft
    });
    navigate('/expenses', { state: { from, to } });
  } catch (err) {
    console.error(err);
    setErrors({ api: "Save failed. Please try again." });
  }
};


const handleSubmit = async () => {
  const e = validateRequired(form);
  setErrors(e);
  if (Object.keys(e).length > 0) return;

  // ✅ NEW EXPENSE (Add page)
  if (!passedExpense) {
    try {
      const payload = buildCreatePayload();
      const created = await createExpense(payload);

      // If backend returns the new id, immediately submit it
      const newExpenseId = created?.expenseId ?? created?.id;
      if (newExpenseId) {
        await updateExpenseStatus({ expenseId: newExpenseId, status: 2 }); // 2 = pending
      }

      navigate('/expenses');
    } catch (err) {
      console.error(err);
      setErrors({ api: "Submit failed. Please try again." });
    }
    return;
  }

  // ✅ EXISTING EXPENSE (Edit page)
  try {
    await updateExpenseStatus({
      expenseId: passedExpense._raw.expenseId,
      status: 2, // pending
    });
    navigate('/expenses', { state: { from, to } });
  } catch (err) {
    console.error(err);
    setErrors({ api: "Submit failed. Please try again." });
  }
};


  const handleCancel = () => {
    navigate('/expenses', { state: { from, to } });
  };

  return (
    <div className="exp-edit-container">
      <h2 className="exp-edit-title">Expense Information</h2>

      <div
        className="exp-edit-status"
        style={{
          background: statusColors[form.status] || '#bdbdbd',
          display: 'inline-block',
          marginRight: 12
        }}
      >
        Status: {form.status.charAt(0).toUpperCase() + form.status.slice(1)}
      </div>

      {id && (
        <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'middle', marginRight: 16 }}>
          ID: {id}
        </span>
      )}

     <span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'middle', marginRight: 16 }}>
  Org Name: {header.orgName || "-"}
</span>
<span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'middle', marginRight: 16 }}>
  Cost Center: {header.costCenter || "-"}
</span>
<span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'middle', marginRight: 16 }}>
  Dept Name: {header.deptName || "-"}
</span>
<span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'middle', marginRight: 16 }}>
  User: {header.userName || "-"}
</span>
<span style={{ fontWeight: 500, color: '#888', fontSize: 15, verticalAlign: 'middle' }}>
  Approver: {header.approverName || "-"}
</span>


      <form onSubmit={e => e.preventDefault()}>
        <div className="exp-edit-form-section">
          <div className="exp-edit-form-row">
           <div className="exp-edit-form-group">
  <label>Project:</label>

  <select
    name="project"
    value={form.project}
    onChange={handleChange}
    disabled={isEditingDisabled}
    className="exp-edit-input"
  >
    <option value="">Select Project</option>

    {(projectsToUse.length > 0 ? projectsToUse : projectOptions).map((p) => {
      // If fallback string list
      if (typeof p === "string") {
        return (
          <option key={p} value={p}>
            {p}
          </option>
        );
      }

      const value = p?.projectCode ?? String(p?.projectId ?? "");
      const label = p?.projectCode
        ? `${p.projectCode}${p.description ? ` - ${p.description}` : ""}`
        : (p?.description ?? value);

      return (
        <option key={p?.projectId ?? value} value={value}>
          {label}
        </option>
      );
    })}
  </select>
</div>
 

            <div className="exp-edit-form-group">
              <label>Expense Type:</label>
              <select
                name="expenseType"
                value={form.expenseType}
                onChange={handleChange}
                disabled={isEditingDisabled}
                className="exp-edit-input"
              >
                <option value="">Select Expense Type</option>
               {expenseTypeOptions.map((t) => {
  const value =
    t?.expenseTypeName ??
    t?.expTypeName ??
    t?.typeName ??
    t?.name ??
    "";

  const key =
    t?.expenseTypeId ??
    t?.expTypeId ??
    t?.expenseTypeCode ??
    t?.code ??
    value;

  return (
    <option key={key} value={value}>
      {value}
    </option>
  );
})}


              </select>
            </div>
          </div>

          <div className="exp-edit-form-row">
            <div className="exp-edit-form-group">
              <label>Amount ($):</label>
              <input
                type="text"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                disabled={isEditingDisabled}
                className="exp-edit-input"
                min="0"
                step="0.01"
                placeholder="0.00"
                pattern="^\\d+(\\.\\d{1,2})?$"
                maxLength={12}
              />
            </div>

            <div className="exp-edit-form-group">
              <label>Date:</label>
              <DatePicker
                selected={form.date}
                onChange={handleDateChange}
                disabled={isEditingDisabled}
                className="exp-edit-input"
                placeholderText="Select date"
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>

          <div className="exp-edit-form-group">
            <label>
              Description: <span style={{ fontWeight: 500, color: '#888', fontSize: 13 }}> (max 512 characters)</span>
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              disabled={isEditingDisabled}
              className="exp-edit-input exp-edit-textarea"
              placeholder="Enter expense description..."
              rows="3"
              maxLength={512}
            />
            {(errors.project || errors.expenseType || errors.amount || errors.date) && (
  <div style={{ color: "red", marginTop: 6, fontSize: 13 }}>
    {errors.project && <div>{errors.project}</div>}
    {errors.expenseType && <div>{errors.expenseType}</div>}
    {errors.amount && <div>{errors.amount}</div>}
    {errors.date && <div>{errors.date}</div>}
  </div>
)}

{errors.api && (
  <div style={{ color: "red", marginTop: 6, fontSize: 13 }}>
    {errors.api}
  </div>
)}

          </div>
        </div>

        <div className="exp-edit-btn-row">
          {!isEditingDisabled && (
            <>
              <button type="button" onClick={handleSave} className="exp-edit-btn save">
                Save
              </button>
              <button type="button" onClick={handleSubmit} className="exp-edit-btn submit">
                Submit
              </button>
            </>
          )}
          <button type="button" onClick={handleCancel} className="exp-edit-btn cancel">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseEditPage;
